import React, { useContext, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { apiUrl } from '../config/api';
import { AuthContext } from '../context/AuthContext';
import { getCompanyConfigCached } from '../utils/invoiceUtils';
import { useTheme } from '../context/ThemeContext';
import { useEnvironment } from '../hooks/useEnvironment';

const Login = () => {
  const { user, setUser } = useContext(AuthContext);
  const navigate = useNavigate();
  const { tenantSlug } = useParams();
  const { theme } = useTheme();
  const env = useEnvironment();
  const isDark = theme === 'dark';
  const [tenantInfo, setTenantInfo] = useState(null);
  const [tenantSlugInvalid, setTenantSlugInvalid] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [twoFAStage, setTwoFAStage] = useState(false);
  const [code, setCode] = useState('');
  const [error, setError] = useState(null);
  const [logoUrl, setLogoUrl] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [placeholderEmail, setPlaceholderEmail] = useState('user@example.com');
  const [countdown, setCountdown] = useState(0);
  const [companyMissing, setCompanyMissing] = useState(true);
  const [unverifiedEmail, setUnverifiedEmail] = useState('');
  const [resendingVerify, setResendingVerify] = useState(false);
  const [resendVerifyMsg, setResendVerifyMsg] = useState('');

  // If already logged in, don't allow staying on the login page.
  useEffect(() => {
    if (!user) return;
    const target = user.mustChangePassword ? '/change-password' : '/dashboard';
    navigate(target, { replace: true });
  }, [user, navigate]);

  useEffect(() => {
    let timer;
    if (countdown > 0) {
      timer = setTimeout(() => setCountdown(countdown - 1), 1000);
    }
    return () => clearTimeout(timer);
  }, [countdown]);

  // When the URL carries a tenant slug (/:tenantSlug/login), look it up so
  // the login card shows the company name above the form. If the slug
  // doesn't exist, surface a 404-style message instead of silently
  // pretending the URL was valid.
  useEffect(() => {
    if (!tenantSlug) {
      setTenantInfo(null);
      setTenantSlugInvalid(false);
      return;
    }
    let active = true;
    (async () => {
      try {
        const res = await axios.get(apiUrl(`/api/tenants/by-slug/${encodeURIComponent(tenantSlug)}`));
        if (active) {
          setTenantInfo(res.data || null);
          setTenantSlugInvalid(false);
        }
      } catch (err) {
        if (active) {
          setTenantInfo(null);
          setTenantSlugInvalid(err?.response?.status === 404);
        }
      }
    })();
    return () => { active = false; };
  }, [tenantSlug]);

  useEffect(() => {
    (async () => {
      try {
        const cfg = await getCompanyConfigCached();
        setCompanyMissing(!!cfg?.missing);
        // In multi-tenant mode (any environment that allows self-registration)
        // the login page is shared across all tenants, so a single
        // "company" logo/name doesn't apply. Show neutral platform branding
        // and skip the tenant-specific values from /api/company-config/public.
        const multiTenant = env?.allowsSelfRegistration;
        if (!multiTenant && cfg?.company_name) setCompanyName(cfg.company_name);
        if (!multiTenant && cfg?.logo_base64) {
          setLogoUrl(cfg.logo_base64);
        } else {
          setLogoUrl('');
        }
        if (!multiTenant && cfg?.email && cfg.email.includes('@')) {
          const domain = cfg.email.split('@')[1];
          setPlaceholderEmail(`user@${domain}`);
        }
      } catch {}
    })();
  }, [env?.allowsSelfRegistration]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError(null);
    setUnverifiedEmail('');
    setResendVerifyMsg('');
    setLoading(true);
    try {
      const res = await axios.post(apiUrl('/api/auth/login'), { email, password });
      if (res.data.twofaRequired) {
        setTwoFAStage(true);
        setCountdown(600); // Start 10-minute countdown for code expiry
      } else if (res.data.token) {
        handleSuccessfulLogin(res.data);
      } else {
        setError('Unexpected response.');
      }
    } catch (err) {
      const data = err.response?.data || {};
      if (data.emailVerificationRequired) {
        setUnverifiedEmail(data.email || email);
        setError(data.error || 'Please verify your email before signing in.');
      } else {
        setError(data.error || err.message || 'Login failed');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResendVerification = async () => {
    if (!unverifiedEmail) return;
    setResendingVerify(true);
    setResendVerifyMsg('');
    try {
      const res = await axios.post(apiUrl('/api/auth/resend-verification'), { email: unverifiedEmail });
      setResendVerifyMsg(res.data?.message || 'Verification link resent.');
    } catch (err) {
      setResendVerifyMsg(err.response?.data?.error || 'Could not resend verification email.');
    } finally {
      setResendingVerify(false);
    }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const res = await axios.post(apiUrl('/api/auth/verify-2fa'), { email, code });
      handleSuccessfulLogin(res.data);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Verification failed');
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    setError(null);
    try {
      await axios.post(apiUrl('/api/auth/resend-2fa'), { email });
      setCountdown(600); // Restart 10-minute countdown
    } catch (e) {
      setError('Failed to resend code.');
    }
  };

  const handleBackToLogin = () => {
    setTwoFAStage(false);
    setCode('');
    setError(null);
    setCountdown(0);
    setPassword(''); // For security
  };

  const handleSuccessfulLogin = (data) => {
    localStorage.setItem('token', data.token);
    const userData = {
      id: data.id,
      roleId: data.roleId,
      mustChangePassword: data.mustChangePassword || false,
      email: data.email,
      permissions: data.permissions || []
    };
    setUser(userData);
    navigate(userData.mustChangePassword ? '/change-password' : (userData.permissions.includes('SignDocuments') || userData.permissions.includes('ViewProfile') || userData.permissions.includes('ClientDashboard') ? '/dashboard' : '/admin'));
  };

  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds < 10 ? '0' : ''}${remainingSeconds}`;
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center p-4 relative overflow-hidden"
      style={{ background: 'linear-gradient(145deg, #060c1a 0%, #0d1f3c 30%, #162447 55%, #1a1a5e 80%, #2d1b69 100%)' }}
    >
      {/* Atmospheric blobs */}
      <div className="absolute pointer-events-none" style={{ top: '-10%', left: '-8%', width: 600, height: 600, borderRadius: '50%', background: 'radial-gradient(circle, rgba(59,130,246,0.18) 0%, transparent 65%)' }} />
      <div className="absolute pointer-events-none" style={{ bottom: '-12%', right: '-8%', width: 650, height: 650, borderRadius: '50%', background: 'radial-gradient(circle, rgba(99,102,241,0.2) 0%, transparent 65%)' }} />
      <div className="absolute pointer-events-none" style={{ top: '15%', right: '5%', width: 350, height: 350, borderRadius: '50%', background: 'radial-gradient(circle, rgba(139,92,246,0.12) 0%, transparent 65%)' }} />
      <div className="absolute pointer-events-none" style={{ bottom: '20%', left: '5%', width: 300, height: 300, borderRadius: '50%', background: 'radial-gradient(circle, rgba(14,165,233,0.1) 0%, transparent 65%)' }} />

      {/* Subtle dot-grid texture */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage: 'radial-gradient(rgba(255,255,255,0.06) 1px, transparent 1px)',
          backgroundSize: '28px 28px',
        }}
      />

      {/* Thin horizontal line accents */}
      <div className="absolute inset-x-0 top-1/3 h-px pointer-events-none" style={{ background: 'linear-gradient(90deg, transparent, rgba(99,102,241,0.15), transparent)' }} />
      <div className="absolute inset-x-0 bottom-1/3 h-px pointer-events-none" style={{ background: 'linear-gradient(90deg, transparent, rgba(59,130,246,0.12), transparent)' }} />

      <div className="w-full max-w-[420px] relative z-10">

        {/* Card */}
        <div
          className="rounded-3xl overflow-hidden"
          style={{
            background: isDark ? 'rgba(15,23,42,0.97)' : 'rgba(255,255,255,0.985)',
            boxShadow: isDark
              ? '0 40px 80px -16px rgba(0,0,0,0.8), 0 0 0 1px rgba(255,255,255,0.05), 0 0 100px -30px rgba(99,102,241,0.3)'
              : '0 40px 80px -16px rgba(0,0,0,0.55), 0 0 0 1px rgba(255,255,255,0.1), 0 0 100px -30px rgba(99,102,241,0.4)',
          }}
        >
          {/* Top gradient accent bar */}
          <div className="h-[3px]" style={{ background: 'linear-gradient(90deg, #3b82f6 0%, #6366f1 40%, #8b5cf6 70%, #a855f7 100%)' }} />

          {/* Header section */}
          <div className="px-8 pt-9 pb-6 flex flex-col items-center text-center">

            {/* Logo container with soft glow ring. For the demo NCLMS logo
                (dark text on transparent background), force an opaque white
                inner panel so the dark text stays legible against the dark
                login card. */}
            <div
              className="relative mb-5 flex items-center justify-center rounded-2xl p-4"
              style={{
                background: env?.allowsSelfRegistration
                  ? '#ffffff'
                  : (isDark ? 'linear-gradient(135deg, rgba(59,130,246,0.1), rgba(99,102,241,0.1))' : 'linear-gradient(135deg, rgba(59,130,246,0.06), rgba(99,102,241,0.06))'),
                border: `1px solid ${isDark ? 'rgba(99,102,241,0.2)' : 'rgba(99,102,241,0.1)'}`,
                boxShadow: env?.allowsSelfRegistration && isDark
                  ? '0 8px 24px -8px rgba(0,0,0,0.6), inset 0 0 0 1px rgba(255,255,255,0.04)'
                  : undefined,
              }}
            >
              {env?.allowsSelfRegistration ? (
                // Demo / multi-tenant install: platform NCLMS logo (lives
                // in client/public/, served at /nclms_logo.png). Falls back
                // to a styled NCLMS text mark if the image fails to load.
                <>
                  <img
                    src={`${process.env.PUBLIC_URL || ''}/nclms_logo.png`}
                    alt="NCLMS — Namibia Cash Loan Management System"
                    className="h-20 sm:h-24 w-auto max-w-[260px] object-contain relative z-10"
                    onError={(e) => {
                      e.currentTarget.style.display = 'none';
                      const fallback = e.currentTarget.nextElementSibling;
                      if (fallback) fallback.style.display = 'block';
                    }}
                  />
                  <div
                    className="text-4xl sm:text-5xl font-extrabold tracking-tight leading-none relative z-10 select-none"
                    style={{ display: 'none' }}
                    aria-label="NCLMS"
                  >
                    <span className="text-slate-900 dark:text-slate-100">NCLM</span>
                    <span style={{ color: '#10b981' }}>S</span>
                  </div>
                </>
              ) : logoUrl ? (
                <img
                  src={logoUrl}
                  alt={`${companyName} Logo`}
                  className="h-20 sm:h-24 w-auto max-w-[240px] object-contain relative z-10"
                  onError={() => setLogoUrl('')}
                />
              ) : (
                <div className="h-20 w-20 sm:h-24 sm:w-24 rounded-xl bg-white/80 dark:bg-slate-700/80 text-indigo-700 dark:text-indigo-300 font-bold text-2xl flex items-center justify-center relative z-10">
                  {String(companyName || 'C').charAt(0).toUpperCase()}
                </div>
              )}
            </div>

            <h2
              className="text-[22px] font-bold tracking-tight"
              style={{
                background: isDark ? 'linear-gradient(135deg, #93c5fd, #818cf8)' : 'linear-gradient(135deg, #1e3a8a, #4f46e5)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
              }}
            >
              {twoFAStage ? 'Verify Your Identity' : 'Sign in to your account'}
            </h2>
            {!twoFAStage && tenantInfo?.companyName && (
              <p className="mt-1 text-xs sm:text-sm font-semibold tracking-wide text-indigo-700 dark:text-indigo-300">
                {tenantInfo.companyName}
              </p>
            )}
            {!twoFAStage && tenantSlugInvalid && (
              <p className="mt-2 text-xs text-red-600 dark:text-red-400 font-medium">
                Workspace <span className="font-mono">/{tenantSlug}</span> does not exist. Sign in below to be routed by email.
              </p>
            )}
            {!twoFAStage && (
              <p className="mt-1.5 text-sm text-gray-500 dark:text-slate-400 font-medium">Enter your credentials to continue</p>
            )}
          </div>

          {/* Setup notice */}
          {!twoFAStage && companyMissing && (
            <div className="mx-8 -mt-2 mb-4 text-center text-xs text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800/40 rounded-xl py-2.5 px-3">
              First time installing?{' '}
              <Link to="/setup" className="font-semibold text-amber-800 dark:text-amber-300 hover:underline">Run setup</Link>
            </div>
          )}

          {/* Form area */}
          <div className="px-8 pb-8">
            {!twoFAStage ? (
              <form onSubmit={handleLogin} className="space-y-5">

                {/* Email field */}
                <div>
                  <label className="block text-sm font-medium text-gray-800 dark:text-slate-200 mb-1">Email Address</label>
                  <div className={`flex items-center bg-white dark:bg-slate-800 border border-gray-300 dark:border-slate-500 rounded transition-colors focus-within:ring-2 ${email ? 'border-emerald-300 dark:border-emerald-700/60 focus-within:border-emerald-500 focus-within:ring-emerald-500/20' : 'border-rose-300 dark:border-rose-700/60 focus-within:border-rose-500 focus-within:ring-rose-500/20'}`}>
                    <span className="pl-3.5 pointer-events-none flex-shrink-0">
                      <svg className="w-4 h-4 text-gray-400 dark:text-slate-500" fill="none" stroke="currentColor" strokeWidth={1.75} viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" />
                      </svg>
                    </span>
                    <input
                      type="email"
                      value={email}
                      onChange={e => setEmail(e.target.value)}
                      required
                      className="flex-1 pl-2.5 pr-3 bg-transparent"
                      placeholder={placeholderEmail}
                    />
                  </div>
                </div>

                {/* Password field */}
                <div>
                  <label className="block text-sm font-medium text-gray-800 dark:text-slate-200 mb-1">Password</label>
                  <div className={`flex items-center bg-white dark:bg-slate-800 border border-gray-300 dark:border-slate-500 rounded transition-colors focus-within:ring-2 ${password ? 'border-emerald-300 dark:border-emerald-700/60 focus-within:border-emerald-500 focus-within:ring-emerald-500/20' : 'border-rose-300 dark:border-rose-700/60 focus-within:border-rose-500 focus-within:ring-rose-500/20'}`}>
                    <span className="pl-3.5 pointer-events-none flex-shrink-0">
                      <svg className="w-4 h-4 text-gray-400 dark:text-slate-500" fill="none" stroke="currentColor" strokeWidth={1.75} viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z" />
                      </svg>
                    </span>
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={e => setPassword(e.target.value)}
                      required
                      className="flex-1 pl-2.5 pr-2 bg-transparent"
                      placeholder="••••••••"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(v => !v)}
                      className="pr-3.5 text-gray-400 dark:text-slate-500 hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors flex-shrink-0"
                      tabIndex={-1}
                      title={showPassword ? 'Hide password' : 'Show password'}
                    >
                      {showPassword ? (
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={1.75} viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88" />
                        </svg>
                      ) : (
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={1.75} viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
                          <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                      )}
                    </button>
                  </div>
                </div>

                {/* Submit button */}
                <div className="pt-1">
                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full text-white py-3 px-4 rounded-xl font-semibold text-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-60 transition-all flex items-center justify-center gap-2"
                    style={{
                      background: loading ? 'linear-gradient(135deg, #4f46e5, #7c3aed)' : 'linear-gradient(135deg, #2563eb 0%, #4f46e5 50%, #7c3aed 100%)',
                      boxShadow: loading ? 'none' : '0 4px 20px rgba(79,70,229,0.45), 0 2px 8px rgba(0,0,0,0.15)',
                    }}
                    onMouseEnter={e => { if (!loading) e.currentTarget.style.boxShadow = '0 6px 28px rgba(79,70,229,0.6), 0 2px 8px rgba(0,0,0,0.2)'; }}
                    onMouseLeave={e => { e.currentTarget.style.boxShadow = loading ? 'none' : '0 4px 20px rgba(79,70,229,0.45), 0 2px 8px rgba(0,0,0,0.15)'; }}
                  >
                    {loading ? (
                      <>
                        <svg className="animate-spin h-4 w-4 text-white/80" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                        </svg>
                        Signing in…
                      </>
                    ) : 'Sign In'}
                  </button>
                </div>
              </form>

            ) : (
              <form onSubmit={handleVerify} className="space-y-5">

                {/* 2FA info box */}
                <div
                  className="rounded-xl p-4 text-center"
                  style={{
                    background: isDark ? 'linear-gradient(135deg, rgba(59,130,246,0.1), rgba(99,102,241,0.1))' : 'linear-gradient(135deg, rgba(59,130,246,0.06), rgba(99,102,241,0.06))',
                    border: `1px solid ${isDark ? 'rgba(99,102,241,0.3)' : 'rgba(99,102,241,0.15)'}`,
                  }}
                >
                  <div className="flex justify-center mb-2">
                    <div className="w-8 h-8 rounded-full bg-indigo-100 dark:bg-indigo-900/40 flex items-center justify-center">
                      <svg className="w-4 h-4 text-indigo-600 dark:text-indigo-400" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                    </div>
                  </div>
                  <p className="text-sm text-blue-800 dark:text-blue-300 font-medium">
                    {/* The address the code went to: the one signed in with. */}
                    A code has been sent to{' '}
                    {email.trim()
                      ? <span className="font-bold text-indigo-700 dark:text-indigo-400 break-all">{email.trim()}</span>
                      : 'your email'}
                    . It will expire in{' '}
                    <span className="font-bold text-indigo-700 dark:text-indigo-400">{formatTime(countdown)}</span>.
                  </p>
                </div>

                {/* Code input */}
                <div>
                  <label className="block text-sm font-medium text-gray-800 dark:text-slate-200 mb-1">Verification Code</label>
                  <input
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    value={code}
                    onChange={e => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    required
                    className={`w-full py-3.5 ${code ? '' : 'border-rose-300 dark:border-rose-700/60 focus:border-rose-500 focus:ring-rose-500/20'} tracking-[0.6em] text-center text-2xl font-bold`}
                    placeholder="• • • • • •"
                  />
                </div>

                {/* Back / Resend links */}
                <div className="flex justify-between items-center text-sm">
                  <button
                    type="button"
                    onClick={handleBackToLogin}
                    className="text-gray-500 dark:text-slate-400 hover:text-indigo-700 dark:hover:text-indigo-400 font-semibold transition-colors flex items-center gap-1"
                  >
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18" />
                    </svg>
                    Back to Login
                  </button>
                  <button
                    type="button"
                    onClick={handleResendCode}
                    className="text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-300 font-semibold transition-colors"
                  >
                    Resend code
                  </button>
                </div>

                {/* Submit button */}
                <div className="pt-1">
                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full text-white py-3 px-4 rounded-xl font-semibold text-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-60 transition-all flex items-center justify-center gap-2"
                    style={{
                      background: loading ? 'linear-gradient(135deg, #4f46e5, #7c3aed)' : 'linear-gradient(135deg, #2563eb 0%, #4f46e5 50%, #7c3aed 100%)',
                      boxShadow: loading ? 'none' : '0 4px 20px rgba(79,70,229,0.45), 0 2px 8px rgba(0,0,0,0.15)',
                    }}
                    onMouseEnter={e => { if (!loading) e.currentTarget.style.boxShadow = '0 6px 28px rgba(79,70,229,0.6), 0 2px 8px rgba(0,0,0,0.2)'; }}
                    onMouseLeave={e => { e.currentTarget.style.boxShadow = loading ? 'none' : '0 4px 20px rgba(79,70,229,0.45), 0 2px 8px rgba(0,0,0,0.15)'; }}
                  >
                    {loading ? (
                      <>
                        <svg className="animate-spin h-4 w-4 text-white/80" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                        </svg>
                        Verifying…
                      </>
                    ) : 'Verify Code'}
                  </button>
                </div>
              </form>
            )}

            {/* Error message */}
            {error && (
              <div className="mt-4 flex items-start gap-2.5 p-3.5 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/40 rounded-xl">
                <svg className="w-4 h-4 text-red-500 dark:text-red-400 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
                </svg>
                <p className="text-sm text-red-700 dark:text-red-400 font-medium">{error}</p>
              </div>
            )}

            {/* Email-verification required: offer a quick resend */}
            {unverifiedEmail && (
              <div className="mt-3 flex flex-col items-center gap-1">
                <button
                  type="button"
                  onClick={handleResendVerification}
                  disabled={resendingVerify}
                  className="text-sm font-semibold text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-300 disabled:opacity-60"
                >
                  {resendingVerify ? 'Sending…' : 'Resend verification email'}
                </button>
                {resendVerifyMsg && (
                  <span className="text-xs text-gray-500 dark:text-slate-400 text-center">{resendVerifyMsg}</span>
                )}
              </div>
            )}

            {/* Forgot password */}
            <div className="mt-5 text-center">
              <a href="/forgot-password" className="text-sm text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-300 font-semibold transition-colors">
                Forgot password?
              </a>
            </div>

            {/* Client self-registration — prominent gold CTA. Hidden on the
                demo environment (the demo flow is the 'Create a demo account'
                link below). */}
            {!twoFAStage && !env?.demo && (
              <div className="mt-4">
                <Link
                  to={tenantSlug ? `/${tenantSlug}/register-client` : '/register-client'}
                  className="flex items-center justify-center gap-2 w-full px-4 py-2.5 rounded-lg border-2 border-amber-300 dark:border-amber-500/60 bg-amber-50 hover:bg-amber-100 dark:bg-amber-900/20 dark:hover:bg-amber-900/30 text-amber-800 dark:text-amber-200 font-semibold text-sm shadow-sm transition-colors"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
                  </svg>
                  Don't have an account?&nbsp;<span className="underline underline-offset-2">Register</span>
                </Link>
              </div>
            )}

            {/* Demo-account signup link (only when env permits self-signup) */}
            {!twoFAStage && env?.allowsSelfRegistration && (
              <div className="mt-3 text-center text-sm">
                <span className="text-gray-500 dark:text-slate-400">Want to try it out? </span>
                <Link to="/register" className="font-semibold text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-300">
                  Create a demo account
                </Link>
              </div>
            )}
          </div>

          {/* Security strip */}
          <div
            className="px-8 py-3.5 border-t flex items-center justify-center gap-6"
            style={{
              borderColor: isDark ? 'rgba(99,102,241,0.2)' : 'rgba(99,102,241,0.1)',
              background: isDark ? 'linear-gradient(to right, #0f172a, rgba(15,23,42,0.8), #0f172a)' : 'linear-gradient(to right, #f8fafc, rgba(239,246,255,0.6), #f8fafc)',
            }}
          >
            {[
              {
                label: 'SSL Encrypted',
                icon: (
                  <svg className="w-3.5 h-3.5 text-amber-500 dark:text-amber-400" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z" />
                  </svg>
                ),
              },
              {
                label: 'NAMFISA Regulated',
                icon: (
                  <svg className="w-3.5 h-3.5 text-amber-500 dark:text-amber-400" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z" />
                  </svg>
                ),
              },
              {
                label: 'Secure Login',
                icon: (
                  <svg className="w-3.5 h-3.5 text-amber-500 dark:text-amber-400" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                  </svg>
                ),
              },
            ].map(({ icon, label }) => (
              <div key={label} className="flex items-center gap-1.5">
                {icon}
                <span className="text-[10px] font-semibold text-amber-700/80 dark:text-amber-300/80 uppercase tracking-wider">{label}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Footer text */}
        <p className="mt-6 text-center text-xs" style={{ color: 'rgba(255,255,255,0.4)' }}>
          By signing in you agree to our{' '}
          <a
            href="https://konizafs.com/privacy-policy/"
            target="_blank"
            rel="noopener noreferrer"
            className="font-semibold underline underline-offset-2 hover:text-white"
            style={{ color: 'rgba(255,255,255,0.6)' }}
          >
            Privacy Policy
          </a>
        </p>
        <p className="mt-2 text-center text-xs" style={{ color: 'rgba(255,255,255,0.3)' }}>
          Secured · NAMFISA Regulated Microlender Platform
        </p>
      </div>
    </div>
  );
};

export default Login;
